import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface FlowGuardProps {
    children: React.ReactNode;
    /** The state key to check for (e.g., 'fromReset', 'fromSignup') */
    requiredStateKey: string;
    /** Where to redirect if accessed directly without proper flow state */
    fallbackPath: string;
    /** Optional: Additional state keys that are also valid */
    alternateStateKeys?: string[];
    /** If true, allows authenticated users with incomplete profiles to bypass flow protection (for onboarding pages) */
    allowIncompleteProfile?: boolean;
}

/**
 * Checks if user is authenticated with an incomplete profile
 * Used to allow authenticated users to access onboarding pages
 */
function isAuthenticatedWithIncompleteProfile(): boolean {
    try {
        const authUserStr = localStorage.getItem('authUser');
        const authToken = localStorage.getItem('authToken');

        if (!authUserStr || !authToken) {
            return false;
        }

        const authUser = JSON.parse(authUserStr);
        const profileCompleted = authUser.profile_completed || authUser.profileCompleted;

        // User is authenticated and profile is NOT completed
        return profileCompleted === false;
    } catch {
        return false;
    }
}

/**
 * FlowGuard Component
 * Protects pages that should only be accessible through a specific flow.
 * If the user navigates directly to the page without the required state,
 * they will be redirected to the fallback path.
 *
 * Usage:
 * ```tsx
 * // In the page component:
 * <FlowGuard requiredStateKey="fromReset" fallbackPath="/auth/login">
 *   <ResetSuccessContent />
 * </FlowGuard>
 *
 * // When navigating to this page:
 * navigate('/auth/reset-success', { state: { fromReset: true } });
 * ```
 */
const FlowGuard: React.FC<FlowGuardProps> = ({
    children,
    requiredStateKey,
    fallbackPath,
    alternateStateKeys = [],
    allowIncompleteProfile = false
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isValidAccess, setIsValidAccess] = useState<boolean | null>(null);

    useEffect(() => {
        // Check if authenticated user with incomplete profile should be allowed
        if (allowIncompleteProfile && isAuthenticatedWithIncompleteProfile()) {
            setIsValidAccess(true);
            return;
        }

        const state = location.state as Record<string, unknown> | null;

        // Check if the required state key exists and is truthy (supports both boolean and string values)
        const hasRequiredState = Boolean(state?.[requiredStateKey]);

        // Check if any alternate state keys exist and are truthy
        const hasAlternateState = alternateStateKeys.some(
            key => Boolean(state?.[key])
        );

        if (hasRequiredState || hasAlternateState) {
            setIsValidAccess(true);
        } else {
            // Redirect to fallback path
            setIsValidAccess(false);
            navigate(fallbackPath, { replace: true });
        }
    }, [location.state, requiredStateKey, alternateStateKeys, fallbackPath, allowIncompleteProfile, navigate]);

    // Show nothing while checking or if invalid
    if (isValidAccess === null || isValidAccess === false) {
        return null;
    }

    return <>{children}</>;
};

export default FlowGuard;
